import * as React from "react"
import _ from "lodash"
import { Appearance, ViewStyle } from "react-native"
import { FastField, FieldProps } from "formik"
import { FormControl, FormControlProps } from "../../form-control/form-control"
import { SamplingAverageWeightInput, SamplingAverageWeightInputProps } from "../../input/sampling-average-weight-input/sampling-average-weight-input"
import { TxKeyPath } from "../../../i18n"
import { observer } from "mobx-react-lite"
import { color } from "../../../theme"
import { FC, useCallback, useMemo } from "react"
import { FastFieldProps } from "formik/dist/FastField"
import { InputProps } from "../../input/input"
import { NumeralFieldProps } from "../numeral-field/numeral-field"

export interface SamplingAverageWeightFieldProps extends NumeralFieldProps {
  samplingAverageWeightInputProps?: SamplingAverageWeightInputProps
}

const FORM_CONTROL: ViewStyle = {
  paddingHorizontal: 16,
  // marginBottom: 8,
  paddingBottom: 8,
  backgroundColor: color.palette.white
}

const defaultInput: any = {}
const defaultNextInput: any = {}
const defaultFormControlProps: FormControlProps = {}

const SamplingAverageWeightFormControl: FC<Omit<SamplingAverageWeightFieldProps, "FieldComponent"|"name"> & (FastFieldProps | FieldProps)> = observer(function SamplingAverageWeightFormControl(props) {
  const {
    colorScheme = "light" || Appearance.getColorScheme(), useLabel = true,
    txPrefix, input = defaultInput, nextInput = defaultNextInput,
    formControlProps: { isDisabled, inputProps, ...formControlProps } = defaultFormControlProps,
    numeralInputProps, samplingAverageWeightInputProps,
    field, form: { setFieldValue, setFieldTouched, handleBlur }, meta
  } = props;

  const column = field.name.split('.').pop()

  const formControlInputProps = useMemo<InputProps>(() => ({
    testID: `jala:id/${field.name}-input`,
    autoCapitalize: "none",
    autoComplete: "off",
    autoCorrect: false,
    textContentType: "none",
    keyboardType: "numeric",
    returnKeyType: nextInput[field.name] ? "next" : "go",
    // onSubmitEditing: () => nextInput[field.name] ? nextInput[field.name]() : (isValid && handleSubmit()),
    onSubmitEditing: () => nextInput[field.name] ? nextInput[field.name]() : undefined,
    placeholderTx: (txPrefix && `${txPrefix}.${_.camelCase(column)}Placeholder` as TxKeyPath) || undefined,
    // defaultValue: meta.initialValue,
    value: field.value,
    onBlur: handleBlur(field.name),
    ...inputProps
  }), [column, field.name, field.value, handleBlur, inputProps, nextInput, txPrefix])

  const onChangeNumber = useCallback(async (value) => {
    await setFieldValue(field.name, value)
    await setFieldTouched(field.name)
  }, [field.name, setFieldTouched, setFieldValue])

  return (<FormControl
    isInvalid={(meta.initialValue || meta.touched) && !!meta.error}
    labelTx={useLabel && (txPrefix && `${txPrefix}.${_.camelCase(column)}` as TxKeyPath) || undefined}
    colorScheme={colorScheme}
    inputProps={formControlInputProps}
    errorMessage={meta.error}
    style={FORM_CONTROL}
    isDisabled={isDisabled}
    {...formControlProps}
  >
    {(inputProps) => (
      <SamplingAverageWeightInput
        ref={input[field.name]}
        colorScheme={colorScheme}
        onChangeNumber={onChangeNumber}
        isDisabled={isDisabled}
        {...inputProps}
        {...numeralInputProps}
        {...samplingAverageWeightInputProps}
      />
    )}
  </FormControl>)
})

/**
 * Describe your component here
 */
export const SamplingAverageWeightField = observer(function SamplingAverageWeightField(props: SamplingAverageWeightFieldProps) {
  const {
    FieldComponent = FastField,
    name, colorScheme = "light" || Appearance.getColorScheme()
  } = props

  return (
    <FieldComponent name={name}>
      {(fieldProps) => (
        <SamplingAverageWeightFormControl colorScheme={colorScheme} {...props} {...fieldProps} />
      )}
    </FieldComponent>
  )
})
